<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
    <div class="row">
      <div class="col-12">
        <BaseInput
            v-model="formData.email"
            label="Kunden-E-Mail"
            :required=true
            :validation="v$?.email"
            :messages="v$?.email?.required.$invalid ? v$?.email?.required?.$message : v$?.email?.email?.$message"/>
      </div>
    </div>
    <div class="row" style="border-top: 1px solid #D8D8D8; padding-top: 15px">
      <div class="col-12">
        <p style="margin-bottom: 5px">Bitte wählen Sie den Zeitraum der Besprechungen (Start- und Enddatum)</p>
      </div>
      <div class="col-6">
        <Datepicker
            v-model="formData.startDate"
            :required="true"
            label="Startdatum"
            position="down"
            :validation="v$.startDate"
            :messages="v$.startDate.required.$message"/>
      </div>
      <div class="col-6">
        <Datepicker
            v-model="formData.endDate"
            :required="true"
            label="Enddatum"
            position="down"
            :validation="v$.endDate"
            :messages="v$.endDate.required.$message"/>
      </div>
    </div>

    <div class="row" style="border-top: 1px solid #D8D8D8; padding-top: 15px">
      <div class="col-12">
        <p>Wie lange soll der Link gültig sein? (z. B. 1:15 für 1 Stunde und 15 Minuten)</p>
      </div>
      <div class="col-6">
        <BaseNumber
            v-model="formData.hours"
            :labelStyle="{ text: 'Std.' }"
            label="Stunden"
            :validation="v$.hours"
            :messages="v$.hours.required.$message"/>
      </div>

      <div class="col-6">
        <BaseNumber
            v-model="formData.minutes"
            :labelStyle="{ text: 'Min.' }"
            label="Minuten"
            :validation="v$.minutes"
            :messages="v$.minutes.required.$message"/>
      </div>
    </div>

    <div class="row" style="border-top: 1px solid #D8D8D8; padding-top: 15px">
      <div class="col-12">
        <p>Wenn bereits ein aktiver Link vorhanden ist und Sie einen neuen erstellen möchten, aktivieren Sie bitte die Checkbox.</p>
      </div>
      <div class="col-12">
        <BaseCheckbox
            v-model="formData.overwrite"
            labelPosition="right"
            label="Neuen Link erstellen"/>
      </div>
    </div>

    <div class="d-flex justify-content-center w-100 mt-3">
      <SaveButton
          :label="$t('general.button.save')"
          :loading="isSubmitting"/>
    </div>
  </form>
</template>

<script setup>
import portalPrivateCoachingService from '@/api/Portal/PrivateCoaching';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import {computed, defineEmits, defineProps, onMounted, ref, toRefs} from 'vue';
import {useI18n} from 'vue-i18n';
import BaseInput from "@/components/base/BaseInput.vue";
import {emailRule, requiredRule} from "@/shared/validators/validationRules";
import useVuelidate from "@vuelidate/core";
import Datepicker from "@/components/picker/Datepicker.vue";
import BaseNumber from "@/components/base/BaseNumber.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import SaveButton from '@/components/button/SaveButton.vue';

const {t} = useI18n();
const isLoaded = ref(false);
const isSubmitting = ref(false);
const emit = defineEmits(['update:modelValue', 'update:submitForm']);

const props = defineProps({
  coachingId: {
    type: Number,
    required: true,
  },
  email: {
    type: String,
    required: true,
  },
  startDate: {
    type: String,
    required: true,
  },
  endDate: {
    type: String,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const coachingId = toRefs(props)['coachingId'];
const email = toRefs(props)['email'];
const startDate = toRefs(props)['startDate'];
const endDate = toRefs(props)['endDate'];

const formData = ref({
  coachingId: coachingId.value,
  email: email.value,
  startDate: startDate,
  endDate: endDate,
  hours: '',
  minutes: '',
  overwrite: false
});


const rules = ref({
  email: emailRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  hours: requiredRule(t),
  minutes: requiredRule(t),
});

const computedRules = computed(() => rules.value);
const v$ = useVuelidate(computedRules, formData);

async function submitForm() {

  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (!isDurationValid(formData.value.hours, formData.value.minutes)) {
    await ErrorToast({
      response: {
        data: {
          error: {
            message: 'Bitte geben Sie mindestens Stunden oder Minuten an (nicht beide 0).'
          }
        }
      }
    });
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {

    await portalPrivateCoachingService.createAccessTokenRemoteDigitalSignature(formData.value);

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(`Der Link wurde erstellt und an die E-Mail-Adresse ${formData.value.email} gesendet.`);
  } catch (error) {
    console.log(error)
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }

}


function isDurationValid(hours, minutes) {
  const h = Number(hours || 0);
  const m = Number(minutes || 0);

  return h > 0 || m > 0;
}

onMounted(async () => {
  isLoaded.value = true;
});
</script>
<style scoped>

</style>